import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Crown, Trophy, FileText, Search, Users, LogIn } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const customerHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('customer_token')}`,
});

// Contagem animada: o número "sobe" suavemente até o novo valor
const useCountUp = (target, duration = 1500) => {
  const [value, setValue] = useState(target || 0);
  const prevRef = useRef(target || 0);
  useEffect(() => {
    const start = prevRef.current;
    const end = target || 0;
    if (start === end) return undefined;
    const t0 = performance.now();
    let raf;
    const tick = (now) => {
      const p = Math.min((now - t0) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3); // ease-out
      setValue(Math.round(start + (end - start) * eased));
      if (p < 1) {
        raf = requestAnimationFrame(tick);
      } else {
        prevRef.current = end;
      }
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      prevRef.current = end;
    };
  }, [target, duration]);
  return value;
};

const ClientHostEvent = ({ kakoId }) => {
  const [config, setConfig] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [myVotes, setMyVotes] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [votingFor, setVotingFor] = useState({});
  const [winnersHistory, setWinnersHistory] = useState([]);
  const [myVotesSent, setMyVotesSent] = useState([]);
  const [myVotesReceived, setMyVotesReceived] = useState([]);
  const [jackpot, setJackpot] = useState(null);
  const [votingInProgress, setVotingInProgress] = useState(false);
  const [customer, setCustomer] = useState(null); // conta logada no perfil do cliente

  // ID de referência: conta logada tem prioridade sobre o ID digitado na Home
  const myKakoId = customer?.kako_id || kakoId;
  const isInscribed = myKakoId ? participants.some(p => p.kako_id === myKakoId) : false;

  const [visitedSubTabs, setVisitedSubTabs] = useState(() => {
    const today = new Date().toISOString().split('T')[0];
    const saved = localStorage.getItem('visited_host_event_subtabs');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.date !== today) {
          localStorage.removeItem('visited_host_event_subtabs');
          return {};
        }
        return data.tabs || {};
      } catch {
        return {};
      }
    }
    return {};
  });

  const markSubTabAsVisited = (tabName) => {
    const today = new Date().toISOString().split('T')[0];
    setVisitedSubTabs(prev => {
      const updated = { ...prev, [tabName]: true };
      localStorage.setItem('visited_host_event_subtabs', JSON.stringify({
        date: today,
        tabs: updated
      }));
      return updated;
    });
  };

  useEffect(() => {
    loadConfig();
    loadParticipants();
    loadWinnersHistory();
    loadCustomer();
    loadJackpot();
    // 🔴 AO VIVO: ranking e jackpot atualizam sozinhos a cada 10s
    const liveTimer = setInterval(() => {
      loadParticipants();
      loadJackpot();
    }, 10000);
    return () => clearInterval(liveTimer);
  }, []);

  // Quando confirma a conta logada, carrega votos dela
  useEffect(() => {
    if (customer) {
      loadMyVotes();
      loadMyVoteHistory();
    }
  }, [customer]);

  // Votos recebidos (host inscrito visualizando)
  useEffect(() => {
    if (myKakoId) {
      loadReceivedVotes();
    }
  }, [myKakoId]);

  const loadCustomer = async () => {
    const token = localStorage.getItem('customer_token');
    if (!token) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customer-auth/me`, {
        headers: customerHeaders()
      });
      setCustomer(response.data);
    } catch (error) {
      // Token inválido/expirado: trata como não logado
      setCustomer(null);
    }
  };

  // 🔊 Som curto de moedas quando o Jackpot sobe
  const jackpotSoundRef = useRef(null);
  const prevJackpotRef = useRef(null);
  useEffect(() => {
    const v = jackpot?.jackpot_coins;
    if (v === undefined || v === null) return;
    if (prevJackpotRef.current !== null && v > prevJackpotRef.current) {
      try {
        if (!jackpotSoundRef.current) {
          jackpotSoundRef.current = new Audio('/jackpot-coin.wav');
          jackpotSoundRef.current.volume = 0.55;
        }
        jackpotSoundRef.current.currentTime = 0;
        jackpotSoundRef.current.play().catch(() => {});
      } catch (e) { /* autoplay bloqueado até interação — ignora */ }
    }
    prevJackpotRef.current = v;
  }, [jackpot?.jackpot_coins]);

  const loadJackpot = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/host-event/jackpot`);
      setJackpot(response.data);
    } catch (error) {
      console.error('Erro ao carregar jackpot:', error);
    }
  };

  const loadConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/host-event/config`);
      setConfig(response.data);
    } catch (error) {
      console.error('Erro ao carregar config:', error);
    }
  };

  const loadParticipants = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/host-event/participants`);
      setParticipants(response.data);
    } catch (error) {
      console.error('Erro ao carregar participantes:', error);
    }
  };

  const loadMyVotes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/host-event/my-votes/me`, {
        headers: customerHeaders()
      });
      setMyVotes(response.data);
    } catch (error) {
      console.error('Erro ao carregar meus votos:', error);
    }
  };

  const loadMyVoteHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/host-event/votes/mine`, {
        headers: customerHeaders()
      });
      setMyVotesSent(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar histórico de votos:', error);
    }
  };

  const loadReceivedVotes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/host-event/votes/participant/${myKakoId}`);
      setMyVotesReceived(response.data || []);
    } catch (error) {
      console.error('Erro ao carregar votos recebidos:', error);
    }
  };

  const handleInscribe = async () => {
    if (!customer) {
      toast.error('Faça login no seu perfil do cliente para se inscrever');
      return;
    }
    try {
      await axios.post(`${BACKEND_URL}/api/host-event/inscribe`, {}, { headers: customerHeaders() });
      toast.success('Inscrição realizada com sucesso!');
      loadParticipants();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Entre novamente no seu perfil.');
        setCustomer(null);
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao se inscrever');
      }
    }
  };

  const loadWinnersHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/host-event/winner/history`);
      setWinnersHistory(response.data);
    } catch (error) {
      console.error('Erro ao carregar histórico:', error);
    }
  };

  const handleVote = async (participantKakoId) => {
    const votesCount = parseInt(votingFor[participantKakoId] || 0);

    if (!customer) {
      toast.error('Faça login no seu perfil do cliente para votar');
      return;
    }
    if (votesCount <= 0) {
      toast.error('Digite quantos votos deseja dar');
      return;
    }
    if (votesCount > myVotes.remaining_votes) {
      toast.error(`Você só tem ${myVotes.remaining_votes} voto(s) disponível(is)`);
      return;
    }

    if (votingInProgress) return;
    setVotingInProgress(true);

    try {
      await axios.post(`${BACKEND_URL}/api/host-event/vote`, {
        participant_kako_id: participantKakoId,
        votes_count: votesCount
      }, { headers: customerHeaders() });

      toast.success(`${votesCount} voto(s) registrado(s)!`);
      setVotingFor({ ...votingFor, [participantKakoId]: '' });
      loadParticipants();
      loadMyVotes();
      loadMyVoteHistory();
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Sessão expirada. Entre novamente no seu perfil para votar.');
        setCustomer(null);
      } else {
        toast.error(error.response?.data?.detail || 'Erro ao votar');
      }
    } finally {
      setTimeout(() => setVotingInProgress(false), 1000);
    }
  };

  const filteredParticipants = searchTerm
    ? participants.filter(p =>
        p.kako_nickname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.kako_id.includes(searchTerm)
      )
    : participants;

  // 🎰 Animação de contagem do Jackpot
  const animatedJackpot = useCountUp(jackpot?.jackpot_coins || 0);
  const jackpotRising = animatedJackpot !== (jackpot?.jackpot_coins || 0);

  const JackpotCard = () => (
    <div className="mx-auto w-[min(80vw,320px)] text-center" data-testid="host-event-jackpot">
      <div
        className={`relative rounded-2xl px-5 pt-2 pb-3 overflow-visible jackpot-glow ${jackpotRising ? 'jackpot-flash' : ''}`}
        style={{
          background: 'linear-gradient(180deg, #1a2c7a 0%, #0b1240 100%)',
          border: '2px solid rgba(255,213,74,.75)',
          boxShadow: '0 0 26px rgba(255,200,50,.25), inset 0 1px 0 rgba(255,255,255,.15)',
        }}
      >
        <div
          className="absolute inset-0 rounded-2xl pointer-events-none"
          style={{ background: 'radial-gradient(60% 120% at 50% 0%, rgba(255,213,74,.14), transparent 70%)' }}
        />
        <img
          src="/jackpot-title.webp"
          alt="Jackpot"
          draggable={false}
          className="mx-auto w-[80%] mt-0.5 drop-shadow-[0_6px_16px_rgba(0,0,0,.45)]"
        />
        <div className="flex items-center justify-center gap-1.5 mt-1">
          <img src="/roleta/moeda_amarela.png" alt="" className="w-6 h-6 drop-shadow" />
          <span
            className="text-2xl sm:text-3xl font-black tabular-nums text-yellow-300"
            data-testid="host-event-jackpot-total"
            style={{ textShadow: '0 2px 0 #8a5a00, 0 5px 14px rgba(0,0,0,.55)' }}
          >
            {animatedJackpot.toLocaleString('pt-BR')}
          </span>
        </div>
      </div>
      <style>{`
        .jackpot-glow { animation: jackpot-ambient 2.6s ease-in-out infinite; }
        @keyframes jackpot-ambient {
          0%, 100% { box-shadow: 0 0 26px rgba(255,200,50,.25), inset 0 1px 0 rgba(255,255,255,.15); }
          50% { box-shadow: 0 0 55px rgba(255,213,74,.6), 0 0 95px rgba(255,213,74,.28), inset 0 1px 0 rgba(255,255,255,.22); }
        }
        .jackpot-glow.jackpot-flash { animation: jackpot-pulse 0.7s ease-in-out 2, jackpot-ambient 2.6s ease-in-out infinite; }
        @keyframes jackpot-pulse {
          0%, 100% { box-shadow: 0 0 34px rgba(255,200,50,.28), inset 0 1px 0 rgba(255,255,255,.15); transform: scale(1); }
          50% { box-shadow: 0 0 60px rgba(255,213,74,.85), 0 0 110px rgba(255,213,74,.4), inset 0 1px 0 rgba(255,255,255,.3); transform: scale(1.035); }
        }
        @media (prefers-reduced-motion: reduce) { .jackpot-flash, .jackpot-glow { animation: none; } }
      `}</style>
    </div>
  );

  const LiveBadge = () => (
    <span className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-wider text-white bg-red-600/90 px-2 py-0.5 rounded-full shadow">
      <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>
      AO VIVO
    </span>
  );

  if (!config) return null;
  if (!config.show_inscription && !config.show_voting) return null;

  return (
    <Card
      className="border-yellow-200 relative overflow-hidden"
      style={{
        backgroundImage: 'url(/host-background.png)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay para legibilidade */}
      <div className="absolute inset-0 bg-black/20 z-0"></div>

      <CardHeader className="text-center py-4 relative z-10">
        <img
          src="/host-banner.png"
          alt="MEU HOST PREFERIDO"
          className="max-w-full h-auto mx-auto"
          style={{ maxHeight: '200px' }}
        />
      </CardHeader>
      <CardContent className="relative z-10">
        <Tabs
          defaultValue="regras"
          onValueChange={(value) => markSubTabAsVisited(value)}
        >
          <TabsList className={`grid w-full ${
            (() => {
              let count = 1; // Regras sempre
              if (config.show_inscription) count++;
              if (config.show_voting) count++;
              if (config.show_winner) count++;
              return `grid-cols-${count}`;
            })()
          }`}>
            {/* Aba Regras - SEMPRE VISÍVEL */}
            <TabsTrigger
              value="regras"
              data-testid="host-event-tab-regras"
              className={`data-[state=active]:bg-blue-400 data-[state=active]:text-white data-[state=inactive]:bg-blue-100 data-[state=inactive]:text-blue-700 ${
                !visitedSubTabs.regras ? 'neon-blue' : ''
              }`}
              onClick={() => markSubTabAsVisited('regras')}
            >
              <FileText className="w-4 h-4 mr-2" />
              Regras
            </TabsTrigger>

            {config.show_inscription && (
              <TabsTrigger
                value="inscricao"
                data-testid="host-event-tab-inscricao"
                className={`${
                  config.inscriptions_open
                    ? `data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=inactive]:bg-green-100 data-[state=inactive]:text-green-700 ${!visitedSubTabs.inscricao ? 'neon-green' : ''}`
                    : `data-[state=active]:bg-red-600 data-[state=active]:text-white data-[state=inactive]:bg-red-100 data-[state=inactive]:text-red-700 ${!visitedSubTabs.inscricao ? 'neon-red' : ''}`
                }`}
                onClick={() => markSubTabAsVisited('inscricao')}
              >
                <Users className="w-4 h-4 mr-2" />
                Inscrição
              </TabsTrigger>
            )}

            {config.show_voting && (
              <TabsTrigger
                value="votacao"
                data-testid="host-event-tab-votacao"
                className={`${
                  config.voting_open
                    ? `data-[state=active]:bg-green-600 data-[state=active]:text-white data-[state=inactive]:bg-green-100 data-[state=inactive]:text-green-700 ${!visitedSubTabs.votacao ? 'neon-green' : ''}`
                    : `data-[state=active]:bg-yellow-600 data-[state=active]:text-white data-[state=inactive]:bg-yellow-100 data-[state=inactive]:text-yellow-700 ${!visitedSubTabs.votacao ? 'neon-yellow' : ''}`
                }`}
                onClick={() => markSubTabAsVisited('votacao')}
              >
                <Trophy className="w-4 h-4 mr-2" />
                Votação
              </TabsTrigger>
            )}

            {config.show_winner && (
              <TabsTrigger
                value="winner"
                data-testid="host-event-tab-winner"
                className={`data-[state=active]:bg-pink-600 data-[state=active]:text-white data-[state=inactive]:bg-pink-100 data-[state=inactive]:text-pink-700 ${
                  !visitedSubTabs.winner ? 'neon-pink' : ''
                }`}
                onClick={() => markSubTabAsVisited('winner')}
              >
                <Crown className="w-4 h-4 mr-2" />
                Ganhador
              </TabsTrigger>
            )}
          </TabsList>

          {/* Aba Regras */}
          <TabsContent value="regras" className="mt-4">
            <div className="space-y-4">
              <JackpotCard />

              <div className="prose prose-sm max-w-none whitespace-pre-wrap p-4 bg-gray-50 rounded border">
                {config.rules_text}
              </div>
            </div>
          </TabsContent>

          {/* Aba Inscrição */}
          {config.show_inscription && (
            <TabsContent value="inscricao" className="mt-4">
              <div className="space-y-4">
                <div className="p-4 bg-blue-50 border border-blue-200 rounded flex items-center justify-between">
                  <div>
                    <p className="font-semibold">Status das Inscrições:</p>
                    <p className="text-xs text-gray-600">
                      {participants.length} participante(s) inscrito(s)
                    </p>
                  </div>
                  <Badge className={config.inscriptions_open ? 'bg-green-600' : 'bg-red-600'}>
                    {config.inscriptions_open ? 'ABERTAS' : 'FECHADAS'}
                  </Badge>
                </div>

                {/* Login do perfil é OBRIGATÓRIO para se inscrever */}
                {config.inscriptions_open && !customer && (
                  <Card className="border-2 border-orange-400 bg-orange-50" data-testid="host-event-inscribe-login-required">
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row items-center gap-3">
                        <div className="flex items-center gap-2 text-orange-800 flex-1">
                          <span className="text-2xl">🔒</span>
                          <div>
                            <p className="font-bold">Para se inscrever, entre no seu perfil</p>
                            <p className="text-xs">Somente clientes logados no perfil podem se inscrever no evento.</p>
                          </div>
                        </div>
                        <Button
                          onClick={() => { window.location.href = '/conta'; }}
                          data-testid="host-event-inscribe-login-button"
                          className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto"
                        >
                          <LogIn className="w-4 h-4 mr-2" />
                          Entrar / Criar perfil
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {config.inscriptions_open && customer && !isInscribed && (
                  <Button
                    onClick={handleInscribe}
                    data-testid="host-event-inscribe-button"
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500"
                  >
                    <Crown className="mr-2" />
                    QUERO ME INSCREVER! ({customer.kako_id})
                  </Button>
                )}

                {isInscribed && (
                  <div className="p-4 bg-green-50 border-2 border-green-400 rounded-lg text-center">
                    <p className="text-lg font-bold text-green-700">✅ Você já está inscrito!</p>
                    <p className="text-sm text-gray-600 mt-1">Boa sorte no evento! 🍀</p>
                  </div>
                )}

                <div>
                  <p className="font-semibold mb-2 text-white drop-shadow-lg">Participantes:</p>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {participants.map((p) => (
                      <div key={p.id || p.kako_id} className="flex items-center gap-3 p-2 bg-white border rounded">
                        {p.kako_avatar && (
                          <img src={p.kako_avatar} alt="" className="w-8 h-8 rounded-full" />
                        )}
                        <p className="font-medium">{p.kako_nickname}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>
          )}

          {/* Aba Votação */}
          {config.show_voting && (
            <TabsContent value="votacao" className="mt-4">
              <Tabs defaultValue="votar" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-4">
                  <TabsTrigger value="votar" data-testid="host-event-subtab-votar">🗳️ Votar</TabsTrigger>
                  <TabsTrigger value="historico" data-testid="host-event-subtab-historico">📊 Meus Votos</TabsTrigger>
                </TabsList>

                {/* Sub-aba: Votar */}
                <TabsContent value="votar">
                  <div className="space-y-4">

                    <JackpotCard />
                    {/* Login do perfil do cliente é OBRIGATÓRIO para votar */}
                    {!customer && (
                      <Card className="border-2 border-orange-400 bg-orange-50" data-testid="host-event-login-required">
                        <CardContent className="p-4">
                          <div className="flex flex-col sm:flex-row items-center gap-3">
                            <div className="flex items-center gap-2 text-orange-800 flex-1">
                              <span className="text-2xl">🔒</span>
                              <div>
                                <p className="font-bold">Para votar, entre no seu perfil</p>
                                <p className="text-xs">Somente clientes logados no perfil podem votar. É rápido e protege seus votos!</p>
                              </div>
                            </div>
                            <Button
                              onClick={() => { window.location.href = '/conta'; }}
                              data-testid="host-event-login-button"
                              className="bg-orange-600 hover:bg-orange-700 w-full sm:w-auto"
                            >
                              <LogIn className="w-4 h-4 mr-2" />
                              Entrar / Criar perfil
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Votos Disponíveis = saldo de Azulzinhas (500 = 1 voto) */}
                    {customer && myVotes && (
                      <div
                        data-testid="host-event-my-votes"
                        className="relative overflow-hidden rounded-2xl border border-blue-300/60 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-900 p-4 sm:p-5 text-white shadow-[0_8px_24px_rgba(37,99,235,0.35)]"
                      >
                        <div className="absolute -bottom-8 -left-8 w-32 h-32 rounded-full bg-cyan-300/20 blur-2xl pointer-events-none"></div>
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <p className="text-[10px] sm:text-[11px] font-bold uppercase tracking-[0.2em] text-blue-200">🗳️ Meus votos disponíveis</p>
                            <div className="flex items-end gap-2">
                              <span className="text-4xl sm:text-5xl font-black leading-none drop-shadow">{myVotes.remaining_votes}</span>
                              <span className="text-blue-200 text-xs sm:text-sm mb-1">voto{myVotes.remaining_votes === 1 ? '' : 's'}</span>
                            </div>
                            <p className="text-[10px] sm:text-[11px] text-blue-200/90 mt-1 truncate">Conta: {customer.kako_id}</p>
                          </div>
                          <div className="flex-shrink-0 text-center rounded-xl bg-white/10 backdrop-blur-sm border border-white/20 px-3 sm:px-4 py-2.5">
                            <p className="flex items-center justify-center gap-1 text-[10px] font-semibold text-blue-100 uppercase tracking-wide">
                              <img src="/roleta/azulzinha.png" alt="" className="w-3.5 h-3.5" />
                              Azulzinhas
                            </p>
                            <p className="text-lg sm:text-2xl font-black text-cyan-200">{(myVotes.blue_balance || 0).toLocaleString('pt-BR')}</p>
                            <p className="text-[10px] text-blue-200 font-semibold mt-0.5">500 = 1 voto</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Busca */}
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 w-4 h-4 z-10" />
                      <Input
                        placeholder="Buscar por nome ou ID..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        data-testid="host-event-search-input"
                        className="pl-10 bg-white border-2 border-purple-300 focus:border-purple-500"
                      />
                    </div>

                    {/* Lista de Participantes — Ranking ao vivo */}
                    <div className="flex items-center justify-between px-1">
                      <p className="font-bold text-white drop-shadow-lg text-sm">🏁 Ranking da Votação</p>
                      <LiveBadge />
                    </div>
                    <div className="space-y-3 max-h-96 overflow-y-auto" data-testid="host-event-ranking">
                      {filteredParticipants.map((p, idx) => (
                        <div key={p.id || p.kako_id} className="p-2 sm:p-3 border rounded-lg bg-white transition-all duration-500">
                          <div className="flex items-center justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <span className={`font-bold flex-shrink-0 ${idx < 3 ? 'text-lg sm:text-xl' : 'text-sm'}`}>
                                {idx === 0 && '🥇'}
                                {idx === 1 && '🥈'}
                                {idx === 2 && '🥉'}
                                {idx > 2 && `${idx + 1}º`}
                              </span>
                              {p.kako_avatar && (
                                <img src={p.kako_avatar} alt="" className="w-8 sm:w-10 h-8 sm:h-10 rounded-full flex-shrink-0" />
                              )}
                              <div className="min-w-0 flex-1">
                                <p className="font-bold text-sm sm:text-base truncate">{p.kako_nickname}</p>
                                <p className="text-xs text-gray-600">{p.total_votes} votos</p>
                              </div>
                            </div>

                            {config.voting_open && customer && myVotes && myVotes.remaining_votes > 0 && (
                              <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
                                <Input
                                  type="number"
                                  min="1"
                                  max={myVotes.remaining_votes}
                                  placeholder="Qtd"
                                  value={votingFor[p.kako_id] || ''}
                                  onChange={(e) => {
                                    let value = parseInt(e.target.value) || '';
                                    if (value > myVotes.remaining_votes) {
                                      value = myVotes.remaining_votes;
                                    }
                                    setVotingFor({ ...votingFor, [p.kako_id]: value.toString() });
                                  }}
                                  data-testid={`host-event-vote-input-${p.kako_id}`}
                                  className="w-12 sm:w-16 h-8 text-xs"
                                />
                                <Button
                                  size="sm"
                                  onClick={() => handleVote(p.kako_id)}
                                  disabled={votingInProgress}
                                  data-testid={`host-event-vote-button-${p.kako_id}`}
                                  className="bg-purple-600 h-8 text-xs sm:text-sm px-2 sm:px-4"
                                >
                                  {votingInProgress ? 'Enviando...' : 'Votar'}
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </TabsContent>

                {/* Sub-aba: Meus Votos (Histórico) */}
                <TabsContent value="historico" className="space-y-4">
                  {!customer ? (
                    <Card className="border-2 border-orange-400 bg-orange-50">
                      <CardContent className="p-6">
                        <div className="text-center space-y-3">
                          <span className="text-4xl">🔒</span>
                          <p className="font-bold text-orange-800">Entre no seu perfil</p>
                          <p className="text-sm text-orange-700">
                            Faça login no perfil do cliente para ver seus votos
                          </p>
                          <Button
                            onClick={() => { window.location.href = '/conta'; }}
                            className="bg-orange-600 hover:bg-orange-700"
                          >
                            <LogIn className="w-4 h-4 mr-2" />
                            Entrar / Criar perfil
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ) : (
                    <>
                      {/* Votos Enviados */}
                      <Card className="border-2 border-green-300">
                        <CardHeader className="bg-green-50">
                          <CardTitle className="text-lg">📤 Votos Enviados ({myVotesSent.length})</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4">
                          {myVotesSent.length === 0 ? (
                            <p className="text-gray-500 text-center py-4">Você ainda não votou</p>
                          ) : (
                            <div className="space-y-2">
                              {myVotesSent.map((vote, idx) => (
                                <div key={idx} className="p-3 bg-white border rounded flex justify-between">
                                  <div>
                                    <p className="font-semibold">Votei em: {vote.host_nickname || vote.participant_kako_id}</p>
                                    <p className="text-xs text-gray-600">{new Date(vote.created_at).toLocaleString('pt-BR')}</p>
                                  </div>
                                  <Badge className="bg-green-600">{vote.votes_count}x</Badge>
                                </div>
                              ))}
                            </div>
                          )}
                        </CardContent>
                      </Card>

                      {/* Votos Recebidos - Só para Hosts */}
                      {isInscribed && myVotesReceived.length > 0 && (
                        <Card className="border-2 border-purple-300">
                          <CardHeader className="bg-purple-50">
                            <CardTitle className="text-lg">📥 Votos Recebidos ({myVotesReceived.length})</CardTitle>
                          </CardHeader>
                          <CardContent className="p-4">
                            <div className="space-y-2">
                              {myVotesReceived.map((vote, idx) => (
                                <div key={idx} className="p-3 bg-white border rounded flex justify-between">
                                  <div>
                                    <p className="font-semibold">{vote.voter_nickname || vote.voter_kako_id} votou</p>
                                    <p className="text-xs text-gray-600">{new Date(vote.created_at).toLocaleString('pt-BR')}</p>
                                  </div>
                                  <Badge className="bg-purple-600">{vote.votes_count}x</Badge>
                                </div>
                              ))}
                            </div>
                          </CardContent>
                        </Card>
                      )}
                    </>
                  )}
                </TabsContent>
              </Tabs>
            </TabsContent>
          )}

          {/* Aba Ganhador */}
          {config.show_winner && (
            <TabsContent value="winner" className="mt-4">
              <Tabs defaultValue="atual" className="w-full">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="atual">🏆 Vencedor</TabsTrigger>
                  <TabsTrigger value="history">📜 Edições Anteriores</TabsTrigger>
                </TabsList>

                {/* Pódium TOP 3 */}
                <TabsContent value="atual" className="space-y-4 mt-4">
                  {!jackpot || !(jackpot.positions || []).some(p => p.kako_id) ? (
                    <p className="text-white drop-shadow-lg text-center py-8">Pódium ainda não foi definido</p>
                  ) : (
                    <Card className="border-2 border-yellow-400/70 overflow-hidden" style={{ background: 'linear-gradient(180deg, #1a2c7a 0%, #0b1240 100%)' }}>
                      <CardContent className="p-4 sm:p-6">
                        <h3 className="text-center text-2xl sm:text-3xl font-black text-transparent bg-clip-text bg-gradient-to-b from-yellow-200 to-amber-400 drop-shadow mb-5">👑 PÓDIUM</h3>
                        <div className="flex items-end justify-center gap-2 sm:gap-4" data-testid="host-event-podium">
                          {[1, 0, 2].map((posIdx) => {
                            const pos = (jackpot.positions || [])[posIdx];
                            if (!pos) return null;
                            const isFirst = posIdx === 0;
                            const pedestal = [
                              { h: 'h-24 sm:h-28', bg: 'linear-gradient(180deg,#ffd54a,#b8860b)', medal: '🥇' },
                              { h: 'h-16 sm:h-20', bg: 'linear-gradient(180deg,#e5e7eb,#9ca3af)', medal: '🥈' },
                              { h: 'h-12 sm:h-14', bg: 'linear-gradient(180deg,#e8a869,#8c5424)', medal: '🥉' },
                            ][posIdx];
                            return (
                              <div key={posIdx} className="flex flex-col items-center flex-1 max-w-[130px]" data-testid={`host-event-podium-pos-${posIdx + 1}`}>
                                {pos.kako_id ? (
                                  <>
                                    <div className={`relative flex items-center justify-center ${isFirst ? 'w-[104px] h-[104px] sm:w-[128px] sm:h-[128px]' : 'w-16 h-16 sm:w-20 sm:h-20'}`}>
                                      {pos.avatar ? (
                                        <img src={pos.avatar} alt="" className={`rounded-full object-cover ${isFirst ? 'w-[82px] h-[82px] sm:w-[102px] sm:h-[102px] border-2 border-yellow-300' : 'w-full h-full border-2 border-white/40'}`} />
                                      ) : (
                                        <div className={`rounded-full bg-white/10 flex items-center justify-center text-2xl ${isFirst ? 'w-[82px] h-[82px]' : 'w-full h-full'}`}>👤</div>
                                      )}
                                      {isFirst && (
                                        <img src="/frame-host-winner.png" alt="" className="absolute inset-0 w-full h-full object-contain pointer-events-none" />
                                      )}
                                    </div>
                                    <p className={`font-bold text-white truncate w-full text-center drop-shadow ${isFirst ? 'text-sm sm:text-base mt-1' : 'text-xs sm:text-sm mt-1'}`}>{pos.nickname}</p>
                                    <p className="text-[10px] text-blue-200">{(pos.total_votes || 0).toLocaleString('pt-BR')} votos</p>
                                    <div className="flex items-center gap-1 mt-0.5 mb-1.5">
                                      <img src="/roleta/moeda_amarela.png" alt="" className="w-4 h-4" />
                                      <span className="text-xs sm:text-sm font-black text-yellow-300" style={{ textShadow: '0 1px 0 #8a5a00' }}>
                                        {(pos.amount || 0).toLocaleString('pt-BR')}
                                      </span>
                                    </div>
                                  </>
                                ) : (
                                  <p className="text-blue-200/70 text-xs mb-2">—</p>
                                )}
                                <div className={`w-full ${pedestal.h} rounded-t-lg flex flex-col items-center justify-start pt-1.5 shadow-inner`} style={{ background: pedestal.bg }}>
                                  <span className="text-xl sm:text-2xl leading-none drop-shadow">{pedestal.medal}</span>
                                  <span className="text-[10px] sm:text-xs font-black text-black/60">{pos.percent}%</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </TabsContent>

                {/* Edições Anteriores */}
                <TabsContent value="history" className="space-y-3 mt-4">
                  {winnersHistory.length === 0 ? (
                    <p className="text-white drop-shadow-lg text-center py-8">Nenhum evento anterior</p>
                  ) : (
                    winnersHistory.map((event, idx) => (
                      <Card key={idx} className="border-2">
                        <CardHeader className="bg-purple-50">
                          <CardTitle className="text-lg">🏆 {winnersHistory.length - idx}ª Edição - {event.event_date}</CardTitle>
                        </CardHeader>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3 bg-pink-50 p-3 rounded-lg">
                            {event.winner_avatar && (
                              <img src={event.winner_avatar} alt="" className="w-16 h-16 rounded-full border-2 border-pink-500" />
                            )}
                            <div className="flex-1">
                              <p className="font-bold">👑 {event.winner_nickname}</p>
                              <p className="text-sm text-pink-700 font-semibold">VENCEDOR</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </TabsContent>
              </Tabs>
            </TabsContent>
          )}

        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ClientHostEvent;
